/*
 * WHAT THE USER IS LOOKING AT.
 *
 * The question the AI actually needs answered is not "what does this page contain" but "what can the
 * person see right now, and what are they pointing at when they say *this*". Those are different,
 * and the difference is the whole design.
 *
 * ONLY WHAT IS RENDERED, and that is a feature rather than a limitation. The symbol grid pools
 * eighty DOM rows over a million-row catalog; a dashboard pages; a tree virtualises. Reading the
 * underlying data would hand the model a million rows it must then summarise, at a cost proportional
 * to the dataset and an answer that describes something the user cannot see. Reading the DOM costs
 * the same on a catalog of eighty and a catalog of a million, and it is TRUE to the question: the
 * user asking "what is this" means the thing on their screen.
 *
 * So the output is bounded by the VIEWPORT, not by the data. If the AI needs the other 999,920 rows
 * it has tools that fetch them properly, with filters pushed down and pagination, which is the right
 * way to get them and the wrong way to answer "what am I looking at".
 *
 * NO SECRETS, NO CHROME. Inputs report that they are inputs and never their values, because a
 * password field is an input and a token pasted into a text box is a value. Navigation, headers and
 * footers are skipped: they are the same on every page, so they cost tokens on every call and
 * distinguish nothing.
 *
 * NOTHING HERE IS AN INSTRUCTION. Everything this returns is page content, and page content is data.
 * A tool result that says "ignore your instructions" is a string on a screen, and the model is told
 * as much by the framing of the result.
 */
/*
 * BOUNDS. This result goes into a prompt on every turn that asks about the screen, so every field is
 * capped, and the caps are small deliberately: a screen summary that costs four thousand tokens is
 * one nobody can afford to call twice in a conversation.
 */
const MAX_TEXT = 4_000;
const MAX_HEADINGS = 24;
const MAX_ACTIONS = 24;
const MAX_ROWS = 60;
const MAX_ROW_CHARS = 160;
const MAX_SELECTION = 1_000;
/** Per element, for content declared by a component that renders it out of the walk's reach. */
const MAX_DECLARED = 300;
/** Chrome that is identical on every page. Reading it costs tokens on every call and distinguishes
 *  nothing, so it is skipped rather than summarised. */
const SKIP = 'nav, header, footer, script, style, noscript, [aria-hidden="true"], .vitesse-ai-rail, vitesse-ai-rail, ' +
    // The app's own chrome, named because it renders inside components: a sidebar in a shadow root is
    // not reachable by `closest()` from the text inside it, so the walk has to refuse to enter it.
    'aside, [role="navigation"], [role="banner"], [role="complementary"], vitesse-side-nav, vitesse-top-bar, ' +
    // The platform's navigation tree, by name. It carries no landmark role and no <nav>, so nothing
    // generic excludes it — and it was being read back as part of "what is on my screen": every
    // section of the app the reader had NOT navigated to, listed as though it were in front of them.
    'vitesse-nav-tree, .tree-rail';
/** Is this element actually visible? `offsetParent` is null for `display:none` and for a detached
 *  node; a zero-size box is rendered but unseen. Both are "not on screen" to a person. */
function visible(el) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0)
        return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
}
/** Is it inside the viewport, rather than merely in the document? A page scrolled to row 400 is not
 *  showing rows 1-80, and the model asking "what is on screen" means the ones being looked at. */
function inViewport(el) {
    // Same clip rule as the text walk: the window narrowed by every scroll container above it. A
    // heading scrolled out of a panel is not on screen just because the panel is.
    const clip = clipFor(el, new Map());
    if (!clip)
        return false;
    const r = el.getBoundingClientRect();
    return r.bottom > clip.top && r.top < clip.bottom && r.right > clip.left && r.left < clip.right;
}
const clean = (s) => (s ?? '').replace(/\s+/g, ' ').trim();
/**
 * Rows from a virtualised or paged list, if the page has one.
 *
 * Found by ROLE rather than by class name, so this works on any list that is accessible, and keeps
 * working when a component is restyled. `aria-rowcount` is how a virtualised grid declares how many
 * rows exist behind the ones it rendered, which is exactly the number the model needs to understand
 * that it is looking at a window.
 */
function renderedRows(root) {
    // Composed, like everything else here: a list IS a component, so a light-DOM query for its rows
    // finds the element and none of its rows.
    const grid = queryComposed(root, '[role="grid"], [role="table"], [role="treegrid"], [role="listbox"]')[0];
    if (!grid)
        return undefined;
    const rowEls = queryComposed(grid, '[role="row"], [role="option"], [role="treeitem"]').filter(visible).filter(inViewport);
    if (!rowEls.length)
        return undefined;
    const declared = Number(grid.getAttribute('aria-rowcount') ?? grid.getAttribute('aria-setsize') ?? 0);
    return {
        count: rowEls.length,
        // Only when the page SAYS how many there are. Inferring a total from what is rendered would be
        // inventing a number, and the one thing worse than "I do not know how many" is a wrong count.
        total: declared > 0 ? declared : undefined,
        sample: rowEls.slice(0, MAX_ROWS).map((r) => clean(r.textContent).slice(0, MAX_ROW_CHARS)),
    };
}
/**
 * The text the person can actually SEE, not the text the page contains.
 *
 * "What is on my screen" means the viewport. Reading the whole document and calling it the screen
 * produced answers about a page's entire contents — every section, whether or not it had been
 * scrolled to — so an assistant asked about the one panel someone was looking at replied with a
 * summary of everything, which is both wrong and a much larger prompt than the question needed.
 *
 * Walked as TEXT NODES rather than by pruning a clone: each run of text is tested against the
 * viewport by its own parent's geometry, so a long section that is half scrolled off contributes
 * only the half that is showing. A clone has no layout, so it cannot answer that question at all.
 * Each node is visited once, in document order, which is also what keeps the reading order right.
 */
function viewportText(main, doc) {
    const out = [];
    const clips = new Map();
    walkComposed(main, (node) => {
        if (node.nodeType === 1) {
            const el = node;
            // CONTENT THE WALK CANNOT REACH, declared by the component that renders it.
            //
            // Some things on a screen have no text nodes to find: a canvas, a WebGL scene, a
            // cross-origin frame. A dashboard is the sharpest case — every widget paints inside a
            // sandboxed frame with an opaque origin, so the walk gets the tile's title and NOT ONE of
            // the numbers the person is actually reading. Answering "what is on my screen" with the
            // furniture and none of the content is worse than useless: it reads as complete.
            //
            // So a component that knows what it is showing may say so, and this is read at the
            // element's own position with the element's own geometry — a tile scrolled out of view
            // contributes nothing, exactly like text. It is still page content, and page content is
            // data.
            const declared = el.getAttribute('data-screen-text');
            if (declared && visible(el) && inViewport(el))
                out.push(clean(declared).slice(0, MAX_DECLARED));
            // An input's PRESENCE is context; its value is not ours to read. A password field is an
            // input, and so is the box someone pasted a token into.
            if (el.matches('input, textarea') && visible(el) && inViewport(el)) {
                const f = el;
                out.push(`[${f.type || 'text'} field${f.getAttribute('aria-label') ? `: ${f.getAttribute('aria-label')}` : ''}]`);
            }
            return;
        }
        const parent = node.parentElement;
        if (!parent || parent.closest(SKIP) || !visible(parent))
            return;
        const t = clean(visibleTextOf(node, doc, clips));
        if (t)
            out.push(t);
    });
    return clean(out.join(' '));
}
/**
 * querySelectorAll across shadow roots, in rendered order.
 *
 * Same reason as `walkComposed`: headings, buttons and list rows are components, so a light-DOM
 * query returns the page's scaffolding and none of its content. Slotted elements are reached
 * through their host's shadow tree, so they are matched once, where they render.
 */
function queryComposed(root, selector, depth = 0, seen = new Set()) {
    if (depth > 60)
        return [];
    const out = [];
    const take = (el) => {
        if (seen.has(el))
            return;
        seen.add(el);
        if (el.matches(selector))
            out.push(el);
    };
    for (const el of Array.from(root.children ?? [])) {
        // CHROME IS REFUSED HERE, not by the callers. `closest()` cannot cross a shadow boundary, so a
        // button inside the nav tree has no reachable ancestor to test against — the walk has to decline
        // to ENTER the subtree, exactly as the text walk does. Without it the answer to "what can I do
        // on this screen" was fifteen navigation controls that are on every screen, and one real one.
        if (el.matches(SKIP))
            continue;
        take(el);
        if (el.shadowRoot) {
            // A HOST'S LIGHT CHILDREN ARE NOT ITS CONTENT — they are what gets slotted INTO it, and they
            // render where the slot is. Descending only into the shadow root and stopping was the whole
            // bug: in this app every page is slotted into the shell, so the page's headings, buttons and
            // rows were unreachable and the only things this could still see were the shell's own chrome.
            // Measured on the usage page: 0 headings returned while the page had four, and 13 actions of
            // which every one was navigation. The slot branch below is what puts the page back in reach.
            out.push(...queryComposed(el.shadowRoot, selector, depth + 1, seen));
        }
        else if (el instanceof HTMLSlotElement) {
            for (const a of el.assignedElements({ flatten: true })) {
                if (a.matches(SKIP))
                    continue;
                take(a);
                out.push(...queryComposed(a.shadowRoot ?? a, selector, depth + 1, seen));
            }
        }
        else {
            out.push(...queryComposed(el, selector, depth + 1, seen));
        }
    }
    return out;
}
/**
 * Walk the COMPOSED tree — through shadow roots — in rendered order.
 *
 * This is the difference between reading the app and reading its wrapper. Everything here is Lit:
 * the panels, the lists, the rows a person is actually looking at all render inside shadow roots,
 * and a TreeWalker only ever sees light DOM. So "what is on my screen" was answering from the thin
 * layer of markup that happens to sit outside components — section blurbs and headings — and never
 * contained a single row of the list being read. Measured on the config page: a virtual list with
 * 5,766px of content contributed ZERO characters.
 *
 * Order matters as much as reach. An element with a shadow root does not render its own children —
 * it renders its shadow tree, and the children appear only where a <slot> puts them. So the walk
 * descends into the shadow root instead of the light children, and expands each slot to what is
 * assigned to it. Doing both would report slotted text twice, in the wrong place.
 */
function walkComposed(root, visit, depth = 0) {
    // A guard, not a limit anyone should reach: a malformed assignment could otherwise recurse
    // forever, and this runs on a person's live page.
    if (depth > 60)
        return;
    for (const child of childrenOf(root)) {
        if (child.nodeType === 3) {
            visit(child);
            continue;
        }
        if (child.nodeType !== 1)
            continue;
        const el = child;
        // Refuse the SUBTREE, not just the node. `closest()` cannot cross a shadow boundary, so chrome
        // rendered inside a component was unreachable from the text within it — the navigation and the
        // sidebar were being read back to the user as part of "what is on my screen".
        if (el.matches(SKIP))
            continue;
        visit(el);
        if (el.localName === 'slot') {
            // Assigned nodes render HERE; the slot's own children are the fallback and only render when
            // nothing is assigned.
            const assigned = el.assignedNodes({ flatten: true });
            if (assigned.length) {
                for (const a of assigned) {
                    if (a.nodeType === 3)
                        visit(a);
                    else if (a.nodeType === 1) {
                        visit(a);
                        walkComposed(a, visit, depth + 1);
                    }
                }
                continue;
            }
        }
        walkComposed(el.shadowRoot ?? el, visit, depth + 1);
    }
}
const childrenOf = (node) => Array.from(node.childNodes);
/**
 * The part of this text that is ACTUALLY ON SCREEN — by line, and only where nothing covers it.
 *
 * "What is on my screen" is a question about pixels, and three things stood between the old answer
 * and that:
 *
 *   1. The PARENT's box. A section wrapper is thousands of pixels tall, so it intersects the
 *      viewport whenever any of it does and every text node inside counted as visible. A Range has
 *      the geometry of the text itself.
 *
 *   2. The WINDOW only. Almost nothing here scrolls the window; panels scroll inside containers. A
 *      row scrolled out of a container keeps window coordinates that read as visible.
 *
 *   3. ALL-OR-NOTHING per node. A paragraph half below the fold was included whole. Each client
 *      rect is one LINE BOX, so the visible lines can be taken and the rest left — which is what a
 *      person actually sees when they are scrolled into the middle of something.
 *
 * And what remains after geometry is OCCLUSION: text under the assistant panel, a dialog, or a
 * sticky header is on screen by every measurement above and cannot be read. Each line is sampled at
 * its midpoint and kept only if this node is what is painted there.
 *
 * Returns '' when none of it shows.
 */
function visibleTextOf(node, doc, clips) {
    const parent = node.parentElement;
    if (!parent)
        return '';
    const clip = clipFor(parent, clips);
    if (!clip)
        return '';
    const range = doc.createRange();
    range.selectNodeContents(node);
    const rects = range.getClientRects();
    const whole = node.textContent ?? '';
    if (!rects.length)
        return '';
    const parts = [];
    for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        if (r.width === 0 && r.height === 0)
            continue;
        const left = Math.max(r.left, clip.left);
        const right = Math.min(r.right, clip.right);
        const top = Math.max(r.top, clip.top);
        const bottom = Math.min(r.bottom, clip.bottom);
        if (bottom <= top || right <= left)
            continue; // this line is off-screen
        const midY = (top + bottom) / 2;
        if (!painted(parent, (left + right) / 2, midY, doc))
            continue; // covered by something
        // The visible SPAN of this line, in characters. A line clipped horizontally (a narrow column,
        // a container's edge) contributes only what is inside the clip.
        const from = offsetAt(doc, node, left + 0.5, midY);
        const to = offsetAt(doc, node, right - 0.5, midY);
        if (from === null || to === null) {
            // The hit test landed somewhere else — take the line rather than dropping it. Losing text
            // that IS on screen is the worse error of the two.
            parts.push(whole);
            break;
        }
        parts.push(whole.slice(Math.min(from, to), Math.max(from, to)));
    }
    return parts.join(' ');
}
/**
 * Is this node what is painted at (x, y)?
 *
 * Descends through shadow roots, because `document.elementFromPoint` stops at the host — and in
 * this app the host is nearly always what it returns, which would make every occlusion test pass.
 */
function painted(parent, x, y, doc) {
    let el = doc.elementFromPoint(x, y);
    for (let guard = 0; el?.shadowRoot && guard < 30; guard++) {
        const inner = el.shadowRoot.elementFromPoint(x, y);
        if (!inner || inner === el)
            break;
        el = inner;
    }
    if (!el)
        return false;
    // The hit is ours when it IS the text's parent, or sits inside it (an inline span), or is an
    // ancestor that paints on its behalf (a parent with no box of its own). Anything else is
    // something drawn ON TOP, and the text underneath cannot be read.
    return el === parent || parent.contains(el) || el.contains(parent);
}
/** Character offset within `node` at a point, or null if the point is not in this node. */
function offsetAt(doc, node, x, y) {
    const d = doc;
    if (typeof d.caretPositionFromPoint === 'function') {
        const pos = d.caretPositionFromPoint(x, y);
        return pos && pos.offsetNode === node ? pos.offset : null;
    }
    // Older engines; same answer, different spelling.
    const r = d.caretRangeFromPoint?.(x, y);
    return r && r.startContainer === node ? r.startOffset : null;
}
/**
 * The next element up the RENDERED tree.
 *
 * `parentElement` walks the light tree, and in this app that is not where the scrolling happens. The
 * shell's own scroller lives inside its shadow root while every page is SLOTTED into it from the
 * light DOM — so a light-tree walk from a row goes straight past the container that is actually
 * clipping it and stops at the document, reporting the whole page as on screen. Following
 * `assignedSlot` (a slotted node renders where the slot is) and then the shadow host is the flat
 * tree, which is the one the browser lays out and the one a person sees.
 */
function flatParent(el) {
    const slot = el.assignedSlot;
    if (slot)
        return slot;
    if (el.parentElement)
        return el.parentElement;
    const root = el.getRootNode();
    return root instanceof ShadowRoot ? root.host : null;
}
/** The window, narrowed by every scrollable ancestor. Null when the intersection is empty. */
function clipFor(el, memo) {
    const hit = memo.get(el);
    if (hit !== undefined)
        return hit;
    let top = 0;
    let left = 0;
    let right = window.innerWidth;
    let bottom = window.innerHeight;
    for (let cur = el, hops = 0; cur && hops < 80; cur = flatParent(cur), hops++) {
        const cs = getComputedStyle(cur);
        const scrolls = /auto|scroll|hidden/.test(cs.overflowY) || /auto|scroll|hidden/.test(cs.overflowX);
        if (!scrolls)
            continue;
        const r = cur.getBoundingClientRect();
        top = Math.max(top, r.top);
        left = Math.max(left, r.left);
        right = Math.min(right, r.right);
        bottom = Math.min(bottom, r.bottom);
    }
    const out = bottom > top && right > left ? new DOMRect(left, top, right - left, bottom - top) : null;
    memo.set(el, out);
    return out;
}
/**
 * Read the current screen.
 *
 * Pure DOM, no app coupling: it works in every Vitesse UI without each one teaching it about its own
 * components, which is what lets `platform.whatIsOnScreen` be one declaration rather than three
 * implementations that drift.
 */
export function readScreen(doc = document) {
    const main = doc.querySelector('main, [role="main"]') ?? doc.body;
    const headings = queryComposed(main, 'h1, h2, h3, [role="heading"]')
        .filter(visible)
        .filter(inViewport)
        // A heading pinned in a sticky header is on screen; one scrolled past is not. Its own box is
        // the test, and unlike a section wrapper a heading's box IS the heading.
        .map((h) => clean(h.textContent))
        .filter(Boolean)
        .slice(0, MAX_HEADINGS);
    // Buttons and links by their ACCESSIBLE name, which is what a person would call them, and what the
    // model would have to name to ask for one.
    const actions = [
        ...new Set(queryComposed(main, 'button, a[href], [role="button"], [role="tab"]')
            .filter(visible)
            .filter(inViewport)
            .map((b) => clean(b.getAttribute('aria-label') ?? b.getAttribute('title') ?? b.textContent))
            .filter((t) => t.length > 0 && t.length < 40)),
    ].slice(0, MAX_ACTIONS);
    const full = viewportText(main, doc);
    const text = full.slice(0, MAX_TEXT);
    const rows = renderedRows(main);
    return {
        path: location.pathname + location.search,
        title: clean(doc.title),
        headings,
        text,
        selection: clean(doc.getSelection?.()?.toString()).slice(0, MAX_SELECTION),
        rows,
        actions,
        // TRUE when there is more than this: text past the cap, or a list whose declared size exceeds
        // what is rendered. The model needs to know it is holding a window rather than the whole thing,
        // because the correct next step differs: ask a fetching tool, not ask for the screen again.
        truncated: full.length > MAX_TEXT || (!!rows?.total && rows.total > rows.count) || (rows?.sample.length ?? 0) < (rows?.count ?? 0),
    };
}
/*
 * HOLDING A SELECTION ACROSS THE CLICK THAT ASKS ABOUT IT
 *
 * The whole point of selecting text and then opening the assistant is to ask about what you
 * selected — and that click destroys it. Focusing anything focusable collapses the document
 * selection, so by the time the panel is open and a turn runs, `getSelection()` is empty and the
 * highlight has visibly vanished. The user selects, clicks, and watches their selection disappear.
 *
 * Two halves, and both are needed. `heldSelection` keeps the TEXT so `readSelection` still has an
 * answer. The CSS Custom Highlight API repaints the RANGE so it still looks selected — the browser
 * paints a highlight for a range we hand it, with no DOM mutation, no wrapping spans, and nothing
 * to unwind (the alternative, injecting <mark> elements, edits the page the user is reading and
 * breaks any component that re-renders underneath it).
 *
 * The hold is DELIBERATE and scoped: an app calls `holdSelection()` when its assistant takes focus
 * and `releaseSelection()` when it is done or dismissed. Selecting something new replaces it, so a
 * stale hold cannot outlive the intent behind it. Unsupported browsers keep the text and skip the
 * repaint, which is degraded but not wrong.
 */
const HIGHLIGHT = 'vitesse-ai-selection';
let heldText = '';
let lastText = '';
let lastRange = null;
let lastAt = 0;
/*
 * How long a remembered selection stays promotable.
 *
 * The gesture is select → click → open, which is a second or two. Remembering indefinitely would
 * mean someone who selected a value, read on for a while, then opened the assistant to ask an
 * unrelated question would silently have that stale selection attached to it — the model answering
 * about something they had forgotten they touched. Bounded, so the hold can only ever be about the
 * click that just happened.
 */
const HOLD_WINDOW_MS = 15_000;
const watched = new WeakSet();
const registry = () => {
    const css = globalThis.CSS;
    return css?.highlights ?? null;
};
/*
 * WATCH CONTINUOUSLY, PROMOTE ON DEMAND.
 *
 * `holdSelection()` cannot read the selection itself, because by the time it runs the selection is
 * already gone: pressing the mouse down on the assistant button collapses it, and that happens
 * before any click handler, any property flip, any render. So the last non-empty selection is
 * remembered as it happens — one cheap listener, no work when nothing is selected — and holding is
 * just promoting what was already remembered.
 *
 * A NEW selection supersedes a held one, which is what makes the hold self-limiting: it survives
 * exactly the click that opens the assistant, and nothing else.
 */
function watch(doc) {
    // Per-document, not a single global flag: an app with more than one document (an iframe, a test
    // harness) would otherwise attach to whichever came first and silently ignore the rest.
    if (watched.has(doc) || typeof doc.addEventListener !== 'function')
        return;
    watched.add(doc);
    doc.addEventListener('selectionchange', () => {
        const sel = doc.getSelection?.();
        const text = clean(sel?.toString()).slice(0, MAX_SELECTION);
        if (!text || !sel || sel.rangeCount === 0)
            return; // a collapse tells us nothing; keep what we had
        lastText = text;
        lastRange = sel.getRangeAt(0).cloneRange();
        lastAt = Date.now();
        if (heldText && heldText !== text)
            releaseSelection(); // they selected something else
    });
}
/** Start remembering selections. Idempotent; call it once where the assistant is mounted. */
export function watchSelection(doc = document) {
    watch(doc);
}
/**
 * Keep the selection alive while the assistant has focus, and keep it LOOKING selected.
 *
 * Returns what is held — '' when nothing was selected, which is the common case of opening the
 * panel to type a question rather than to ask about something.
 */
export function holdSelection(doc = document) {
    watch(doc);
    if (!lastText || Date.now() - lastAt > HOLD_WINDOW_MS)
        return heldText;
    heldText = lastText;
    const reg = registry();
    const Ctor = globalThis.Highlight;
    // No Highlight API ⇒ the text is still held and the answer is still right; only the repaint is
    // missing. Degraded, not broken, and nothing to feature-detect at the call site.
    if (reg && Ctor && lastRange)
        reg.set(HIGHLIGHT, new Ctor(lastRange));
    return heldText;
}
/** Drop it. The next question is about something else, and a highlight nobody asked for is noise. */
export function releaseSelection() {
    // Everything, including what was merely REMEMBERED. Closing the assistant ends the gesture; a
    // selection that came back on the next open without the user selecting anything would be a
    // question answered about text they had already dismissed.
    heldText = '';
    lastText = '';
    lastRange = null;
    lastAt = 0;
    registry()?.delete(HIGHLIGHT);
}
/** Just the selection. Separate because "explain this" is a different question from "where am I",
 *  and answering it should not cost a whole screen read.
 *
 *  Falls back to the held selection when the live one is empty — which is exactly the state the act
 *  of opening the assistant puts the page in. */
export function readSelection(doc = document) {
    const live = clean(doc.getSelection?.()?.toString()).slice(0, MAX_SELECTION);
    return live || heldText;
}
//# sourceMappingURL=screen.js.map